import { ManagedReceivingPartnerApi as Api } from "@capstone/mock-api";
import { Day } from "src/app/core/day.model";
import { formatDay } from "src/app/core/pipes/day.pipe";
import { formatWeekdays } from "src/app/core/pipes/weekdays.pipe";
import { Site } from "src/app/core/sites";
import { Weekdays } from "src/app/core/weekdays.model";
import { getApiDetailsDecorator, getUpdatedPropertiesFilter } from "src/utils";

const api = getApiDetailsDecorator<Api.DailyDockCapacity>();

abstract class BaseDailyDockCapacity {
  protected constructor(args: ClassProperties<BaseDailyDockCapacity>) {
    this.appointment = args.appointment;
    this.case = args.case;
    this.pallet = args.pallet;
    this.weekdays = args.weekdays;
  }

  @api({ key: "appointmentCapacity" })
  public readonly appointment: number | null;
  @api({ key: "caseCapacity" }) public readonly case: number | null;
  @api({ key: "palletCapacity" }) public readonly pallet: number | null;
  @api({ key: "dayOfWeek" }) public readonly weekdays: Weekdays;
}

export class DailyDockCapacity extends BaseDailyDockCapacity {
  private constructor(args: ClassProperties<DailyDockCapacity>) {
    super(args);
    this.id = args.id;
  }

  public readonly id: number;

  public static deserialize(
    data: Api.DailyDockCapacity,
    { site }: DeserializeArguments,
  ): DailyDockCapacity | DailyDockCapacityException {
    return data.precedence > 0
      ? DailyDockCapacityException.deserialize(data, { site })
      : new DailyDockCapacity({
          appointment: data.appointmentCapacity ?? null,
          case: data.caseCapacity ?? null,
          id: data.id,
          pallet: data.palletCapacity ?? null,
          weekdays: Weekdays.deserialize(data.dayOfWeek),
        });
  }
}

export class DailyDockCapacityUpdate extends BaseDailyDockCapacity {
  public constructor({ base, ...args }: DailyDockCapacityUpdateArguments) {
    super(args);

    this.base = base;
    this.filterUpdatedProperties = getUpdatedPropertiesFilter(this, base, args);
  }

  public readonly base: DailyDockCapacity | null;
  private readonly filterUpdatedProperties: FilterPartial<Api.DailyDockCapacityUpdate>;

  public serialize(): Partial<Api.DailyDockCapacityUpdate> {
    const dayOfWeek = this.weekdays.serialize();
    if (!dayOfWeek) {
      throw new Error(
        "Daily dock capacities must have at least one selected day.",
      );
    }

    return this.filterUpdatedProperties({
      appointmentCapacity: this.appointment,
      caseCapacity: this.case,
      dayOfWeek,
      endDate: null,
      name: null,
      palletCapacity: this.pallet,
      // Regular, non-exception capacities have a precedence of zero.
      precedence: 0,
      startDate: null,
    });
  }
}

export class DailyDockCapacityModification extends DailyDockCapacityUpdate {
  public constructor({
    ...args
  }: ClassProperties<DailyDockCapacityModification>) {
    super(args);
    this.id = args.id;
  }
  public id: number;
}

export type DailyDockCapacityUpdateArguments =
  ClassProperties<DailyDockCapacityUpdate>;

abstract class BaseDailyDockCapacityException extends BaseDailyDockCapacity {
  protected constructor(args: ClassProperties<BaseDailyDockCapacityException>) {
    super(args);

    this.customName = args.customName;
    this.endDay = args.endDay;
    this.precedence = args.precedence;
    this.startDay = args.startDay;
  }

  @api({ key: "name" }) public readonly customName: string | null;
  @api({ key: "endDate" }) public readonly endDay: Day;
  /**
   * The precedence order (increasing value for higher precedence) that an
   * exception has, with `0` reserved for regular, non-exception capacities.
   */
  @api() public readonly precedence: number;
  @api({ key: "startDate" }) public readonly startDay: Day;
}

export class DailyDockCapacityException extends BaseDailyDockCapacityException {
  private constructor(
    args: Omit<ClassProperties<DailyDockCapacityException>, "name">,
  ) {
    super(args);
    this.id = args.id;

    const formattedStart = formatDay(this.startDay, "en-US", "shortDate");
    const formattedEnd = formatDay(this.endDay, "en-US", "shortDate");
    const formattedWeekdays = formatWeekdays(this.weekdays, "short");
    this.name =
      this.customName ||
      `${formattedStart}–${formattedEnd}: ${formattedWeekdays}`;
  }

  public readonly id: number;
  public readonly name: string;

  public static deserialize(
    data: Api.DailyDockCapacity,
    { site }: DeserializeArguments,
  ): DailyDockCapacityException {
    if (data.precedence === 0) {
      throw new Error(
        "Daily dock capacity exceptions cannot have a zero precedence.",
      );
    }
    if (!data.startDate) {
      throw new Error("Start date required for daily dock capacity.");
    }
    if (!data.endDate) {
      throw new Error("End date required for daily dock capacity.");
    }

    return new DailyDockCapacityException({
      appointment: data.appointmentCapacity ?? null,
      case: data.caseCapacity ?? null,
      customName: data.name || null,
      endDay: Day.deserialize(data.endDate, site.timeZone),
      id: data.id,
      pallet: data.palletCapacity ?? null,
      precedence: data.precedence,
      startDay: Day.deserialize(data.startDate, site.timeZone),
      weekdays: Weekdays.deserialize(data.dayOfWeek),
    });
  }
}

interface DeserializeArguments {
  readonly site: Site;
}

export class DailyDockCapacityExceptionUpdate extends BaseDailyDockCapacityException {
  public constructor({
    base,
    ...args
  }: DailyDockCapacityExceptionUpdateArguments) {
    super(args);

    this.base = base;
    this.name = this.customName;
    this.filterUpdatedProperties = getUpdatedPropertiesFilter(this, base, args);
  }

  public readonly base: DailyDockCapacityException | null;
  public readonly name: string | null;
  private readonly filterUpdatedProperties: FilterPartial<Api.DailyDockCapacityUpdate>;

  public serialize(): Partial<Api.DailyDockCapacityUpdate> {
    const dayOfWeek = this.weekdays.serialize();
    if (!dayOfWeek) {
      throw new Error(
        "Daily dock capacity exceptions must have at least one selected day.",
      );
    }

    return this.filterUpdatedProperties({
      appointmentCapacity: this.appointment,
      caseCapacity: this.case,
      dayOfWeek,
      endDate: this.endDay.toString(),
      name: this.customName,
      palletCapacity: this.pallet,
      precedence: this.precedence,
      startDate: this.startDay.toString(),
    });
  }
}

export type DailyDockCapacityExceptionUpdateArguments = Omit<
  ClassProperties<DailyDockCapacityExceptionUpdate>,
  "name"
>;
